import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAuthSettings, getCloudflareTurnstileSetting } from '@pm/super-library-2/modules/auth';

import { MODULE_TYPE } from '@utils/constants';

export const TURNSTILE = 'CLOUDFLARE_TURNSTILE';

export const TURNSTILE_MODE = {
    NONE: 'NONE',
    STANDARD: 'STANDARD',
    OTP: 'OTP',
};

const turnstileSlice = createSlice({
    name: 'turnstileSlice',
    initialState: {
        turnstileToken: {
            [MODULE_TYPE.LOGIN]: null,
            [MODULE_TYPE.REGISTRATION]: null,
            [MODULE_TYPE.RESET_PASSWORD]: null,
            [MODULE_TYPE.EXTERNAL_REGISTRATION]: null,
            [MODULE_TYPE.AFF_RETRIEVE_USERNAMES]: null,
            [MODULE_TYPE.AFF_LOGIN]: null,
            [MODULE_TYPE.AFF_REGISTRATION]: null,
        },
        site_key: '',
        temp_site_key: '',
        turnstileMode: 'NONE',
        authData: false,
        lastReasons: false,
        isLoading: true,
        // added to avoid spreading undefined in reducers
        turnstileEnabled: {},
        otpTurnstileEnabled: {},
    },
    reducers: {
        setToken: (state, action) => {
            const { token, moduleType } = action.payload;
            state.turnstileToken[moduleType] = token;
        },
        setTurnstileEnabled: (state, action) => {
            // proccessing turnstile
            const { module, moduleEnableTurnstile } = action.payload;
            state.turnstileEnabled = { ...state.turnstileEnabled, [module]: moduleEnableTurnstile };
        },
        setOTPTurnstileEnabled: (state, action) => {
            // proccessing turnstile
            const { module, moduleEnableTurnstile } = action.payload;
            state.otpTurnstileEnabled = { ...state.otpTurnstileEnabled, [module]: moduleEnableTurnstile };
        },
        getSubmitButtonEnabled: (state, action) => {
            const { module } = action.payload;
            state.otpTurnstileEnabled = { ...state.otpTurnstileEnabled, [module]: true };
        },
        clearSiteKey: (state) => {
            // set for refresh
            state.temp_site_key = state.site_key;
            state.site_key = '';
        },
        resetSitekey: (state) => {
            // set for refresh
            state.site_key = state.temp_site_key;
            state.temp_site_key = '';
        },
        setSiteKey: (state, action) => {
            state.site_key = action?.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTurnstileContext.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTurnstileContext.fulfilled, (state, action) => {
                // use site_key (matches thunk return)
                const { site_key, turnstileMode, authData, reasons, moduleType, enabled } = action.payload;

                state.site_key = site_key;
                state.turnstileMode = turnstileMode;
                state.authData = { ...state.authData, [moduleType]: authData };
                state.lastReasons = reasons;
                state.isLoading = false;
                state.enabled = enabled;
            })
            .addCase(fetchTurnstileContext.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'unknown_error';
                state.turnstileMode = TURNSTILE_MODE.NONE;
            });
    },
});

export const fetchTurnstileContext = createAsyncThunk('turnstile/fetchContext', async ({ moduleType = 'LOGIN' }, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        // const authSettingsReducer = state.authSettingsReducer;
        const turnstileState = state.turnstileReducer;
        const language = state.language;

        // 1) Try moduleData first (NO API CALL)
        const cacheData = turnstileState?.authData || false;
        let authData; // storing api result

        // if cache present, use module-specific cached auth data
        if (cacheData?.[moduleType]) {
            authData = cacheData[moduleType] ?? undefined;
        }

        // 2) Fetch ONLY if moduleData missing auth info
        if (!cacheData?.[moduleType]) {
            authData = await getAuthSettings({ isPublic: true, moduleType, currencyCode: localStorage.getItem('currencyLang') || language?.currencyLang, isNew: true });
        }

        // making decision
        const decision = getTurnstileDecision({
            moduleType,
            cacheData,
            authData,
        });

        if (!decision.enabled) {
            return {
                site_key: '',
                turnstileMode: TURNSTILE_MODE.NONE,
                enabled: false,
                authData,
                moduleType,
                reasons: decision.reasons,
            };
        }
        // only fetch site key when decision is enabled
        const site_key = await getSiteKeyCachedOrFetch(turnstileState);

        if (!site_key) {
            return {
                site_key: '',
                turnstileMode: TURNSTILE_MODE.NONE,
                enabled: false,
                authData,
                moduleType,
                reasons: [...decision.reasons, 'empty_site_key'],
            };
        }

        return {
            site_key,
            turnstileMode: decision.turnstileMode,
            enabled: true,
            authData,
            moduleType,
            reasons: decision.reasons,
        };
    } catch (err) {
        return rejectWithValue(err?.message || 'fetch_turnstile_failed');
    }
});

function includesTurnstile(list) {
    return Array.isArray(list) && list.includes(TURNSTILE);
}

export function getTurnstileDecision({ moduleType, cacheData, authData }) {
    const reasons = [];

    // --- LiteApp gate ---
    const isLiteApp = window.SPL_LiteApp?.isLiteApp?.();
    const allowLiteApp = window.portalSettings?.isAllowTurnstileForLiteApp;

    if (isLiteApp && !allowLiteApp) {
        reasons.push('liteapp_skip');
        return { enabled: false, turnstileMode: TURNSTILE_MODE.NONE, reasons };
    }
    // check auth settings reducer
    const moduleCfg = cacheData?.[moduleType] || {};
    const cfgStandardEnabled = !!moduleCfg.turnstileEnabled;
    const cfgOtpEnabled = !!moduleCfg.otpTurnstileEnabled;
    const backendStandardRequired = includesTurnstile(authData?.verify_security_validation) || false;
    const backendOtpRequired = includesTurnstile(authData?.request_security_validation) || false;

    // --- Priority: OTP > STANDARD ---
    if (backendOtpRequired || cfgOtpEnabled) {
        return { enabled: true, turnstileMode: TURNSTILE_MODE.OTP, reasons };
    }

    if (backendStandardRequired || cfgStandardEnabled) {
        return { enabled: true, turnstileMode: TURNSTILE_MODE.STANDARD, reasons };
    }

    return { enabled: false, turnstileMode: TURNSTILE_MODE.NONE, reasons };
}

async function getSiteKeyCachedOrFetch(turnstileState) {
    if (turnstileState.site_key) return turnstileState?.site_key?.site_key || turnstileState?.site_key;
    if (window.portalSettings?.turnstileSiteKey) {
        return window.portalSettings.turnstileSiteKey;
    }
    const site_key = await getCloudflareTurnstileSetting();
    return site_key?.site_key || site_key;
}

export const { setToken, setTurnstileEnabled, setOTPTurnstileEnabled, clearSiteKey, resetSitekey, setSiteKey } = turnstileSlice.actions;
export default turnstileSlice.reducer;
